import { Injectable, signal } from '@angular/core';
import { environment } from '../environments/environment';

/** Counts of each content type a plugin ships, shown as badges on its catalog card. */
export interface PluginContentCounts {
  skills: number;
  agents: number;
  mcpServers: number;
  prompts: number;
}

export interface CatalogPlugin {
  name: string;
  description: string | null;
  category: string | null;
  /** Top-level catalog folder the plugin lives in: "plugins" or "external-plugins". */
  source: string;
  version: string | null;
  ownerTeam: string | null;
  ownerContact: string | null;
  tags: string[];
  keywords: string[];
  contentTypes: string[];
  contentCounts: PluginContentCounts;
  isValid: boolean;
  validationError: string | null;
}

/** One skill (`skills/{name}/SKILL.md`) bundled inside a plugin. */
export interface PluginSkillSummary {
  name: string;
  description: string | null;
}

/** One agent (`agents/*.agent.md`) bundled inside a plugin. */
export interface PluginAgentSummary {
  name: string;
  description: string | null;
  model: string | null;
  tools: string[];
}

/** One MCP server a plugin registers in its `.mcp.json`. */
export interface PluginMcpServerSummary {
  name: string;
  type: 'http' | 'local';
  url: string | null;
  command: string | null;
}

/** Background-only search material that is intentionally excluded from the initial catalog payload. */
export interface PluginSearchDocument {
  pluginName: string;
  skills: PluginSkillSummary[];
  readmeMarkdown: string | null;
}

/** Full detail for one plugin, returned by `GET /api/catalog/{name}`. */
export interface PluginDetail {
  name: string;
  description: string | null;
  category: string | null;
  /** Top-level catalog folder the plugin lives in: "plugins" or "external-plugins". */
  source: string;
  version: string | null;
  ownerTeam: string | null;
  ownerContact: string | null;
  tags: string[];
  keywords: string[];
  contentTypes: string[];
  contentCounts: PluginContentCounts;
  skills: PluginSkillSummary[];
  agents: PluginAgentSummary[];
  mcpServers: PluginMcpServerSummary[];
  readmeMarkdown: string | null;
  lastUpdatedUtc: string | null;
  repositoryUrl: string | null;
  isValid: boolean;
  validationError: string | null;
}

interface CatalogResponse {
  plugins: CatalogPlugin[];
}

interface SearchIndexResponse {
  plugins: PluginSearchDocument[];
}

/**
 * Fetches the real plugin catalog from the backend (`GET /api/catalog`), which reads
 * `.claude-plugin/marketplace.json` from `Chevron/cvx-plugins` on the signed-in user's behalf.
 */
@Injectable({ providedIn: 'root' })
export class CatalogService {
  readonly plugins = signal<CatalogPlugin[]>([]);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly searchDocuments = signal<PluginSearchDocument[]>([]);
  readonly isSearchIndexLoading = signal(false);
  readonly searchIndexError = signal<string | null>(null);

  private loaded = false;
  private searchIndexLoaded = false;
  private searchIndexController: AbortController | undefined;
  private searchIndexGeneration = 0;

  async load(): Promise<void> {
    if (this.loaded || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/catalog`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Catalog request failed with status ${response.status}`);
      }

      const data: CatalogResponse = await response.json();
      this.plugins.set(data.plugins ?? []);
      this.loaded = true;
    } catch {
      this.error.set('An error occurred while loading the plugin catalog.');
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Fetches README and skill search material after the catalog grid is rendered. This remains
   * separate from `load()` so the initial marketplace response does not wait for large GitHub files.
   */
  async loadSearchIndex(): Promise<void> {
    if (this.searchIndexLoaded || this.isSearchIndexLoading()) {
      return;
    }

    const generation = this.searchIndexGeneration;
    const controller = new AbortController();
    this.searchIndexController = controller;
    this.isSearchIndexLoading.set(true);
    this.searchIndexError.set(null);
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/catalog/search-index`, {
        credentials: 'include',
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error(`Search index request failed with status ${response.status}`);
      }

      const data: SearchIndexResponse = await response.json();
      if (generation !== this.searchIndexGeneration) {
        return;
      }

      this.searchDocuments.set(data.plugins ?? []);
      this.searchIndexLoaded = true;
    } catch {
      if (generation === this.searchIndexGeneration) {
        this.searchIndexError.set('Deep search is temporarily unavailable.');
      }
    } finally {
      if (this.searchIndexController === controller) {
        this.searchIndexController = undefined;
        this.isSearchIndexLoading.set(false);
      }
    }
  }

  /**
   * Fetches full detail for one plugin (`GET /api/catalog/{name}`) — its skills, agents, and MCP
   * servers with their own metadata — for the plugin's detail page.
   */
  async loadDetail(name: string): Promise<PluginDetail | null> {
    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/catalog/${encodeURIComponent(name)}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        return null;
      }

      return (await response.json()) as PluginDetail;
    } catch {
      return null;
    }
  }

  /** Clears cached catalog state, e.g. after logout, so it reloads fresh for the next user. */
  reset(): void {
    this.searchIndexGeneration++;
    this.searchIndexController?.abort();
    this.searchIndexController = undefined;
    this.plugins.set([]);
    this.error.set(null);
    this.searchDocuments.set([]);
    this.isSearchIndexLoading.set(false);
    this.searchIndexError.set(null);
    this.loaded = false;
    this.searchIndexLoaded = false;
  }
}
